import { User } from "lucide-react";
import Link from "next/link";
import { ReactNode, useState } from "react";
import { UseFormReturn } from "react-hook-form";

import { FilterValues } from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/filters/filter-schema";
import { ActiveSelection } from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/filters/saved-search-and-preferences/active-selection";
import { setPreferenceDefaultsOptOut } from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/filters/saved-search-and-preferences/preference-defaults.cookie";
import { doesBoardMatchPreferenceState } from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/filters/saved-search-and-preferences/preferences-state-match";
import { PreferencesStateMismatchModal } from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/filters/saved-search-and-preferences/preferences-state-mismatch-modal";
import { usePreferenceFilters } from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/filters/saved-search-and-preferences/use-preference-filters";
import { summarizeFilters } from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/filters/saved-search-and-preferences/utils";
import useStateConfig from "@/hooks/use-state-config";
import { jobFiltersTestIds } from "@/lib/data-test-ids/jobs.data-test-ids";
import routes from "@/lib/routes";
import { IndividualStateConfig } from "@/lib/states-config";
import { cn } from "@/lib/utils";

export enum RowStatus {
  Checked = "checked",
  Unchecked = "unchecked",
  Disabled = "disabled",
}

const PreferencesEditLink = () => {
  return (
    <Link
      href={routes.getProfilePreferences()}
      className="text-[11px] text-ink-subtle hover:text-surface-inverted hover:underline shrink-0"
      data-testid={jobFiltersTestIds.SAVED_SEARCHES_PREFERENCES_EDIT_LINK}
    >
      Edit
    </Link>
  );
};

export const SavedFiltersRow = ({
  icon,
  title,
  subtitle,
  status,
  onToggle,
  testId,
  trailing,
}: {
  icon: ReactNode;
  title: string;
  subtitle: ReactNode;
  status: RowStatus;
  onToggle: (next: boolean) => void;
  testId: string;
  trailing: ReactNode;
}) => {
  const checked = status === RowStatus.Checked;
  const disabled = status === RowStatus.Disabled;

  return (
    <div
      className={cn(
        "group flex w-full items-center gap-2 rounded-md text-left transition-colors cursor-pointer py-2 pr-2",
        checked ? "bg-surface-strong" : "hover:bg-surface-subtle",
      )}
    >
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onToggle(!checked)}
        data-testid={testId}
        className="flex flex-1 min-w-0 items-center text-left gap-2 sm:gap-0"
      >
        <span className="flex size-4 items-center justify-center shrink-0 sm:size-8 sm:rounded-full">
          {icon}
        </span>
        <div className="flex-1 min-w-0">
          <p
            className={cn(
              "text-sm truncate",
              checked ? "font-semibold" : "font-normal",
            )}
          >
            {title}
          </p>
          <p className="text-[11px] text-ink-subtle truncate">{subtitle}</p>
        </div>
      </button>
      {trailing}
    </div>
  );
};

const ActivePreferencesRow = ({
  form,
  filterDefaults,
  preferenceFilters,
  preferenceState,
  activeSelection,
}: {
  form: UseFormReturn<FilterValues>;
  filterDefaults: FilterValues;
  preferenceFilters: FilterValues;
  preferenceState: IndividualStateConfig;
  activeSelection: ActiveSelection;
}) => {
  const currentState = useStateConfig();
  const [showMismatchModal, setShowMismatchModal] = useState(false);

  const status =
    activeSelection.kind === "preferences"
      ? RowStatus.Checked
      : RowStatus.Unchecked;

  const onToggle = (next: boolean) => {
    if (
      next &&
      !doesBoardMatchPreferenceState(currentState, preferenceState.abbreviation)
    ) {
      setShowMismatchModal(true);
      return;
    }

    setPreferenceDefaultsOptOut(!next);
    form.reset(next ? preferenceFilters : filterDefaults);
  };

  return (
    <>
      <SavedFiltersRow
        icon={<User className="stroke-ink-subtle size-4" />}
        title="Your preferences"
        subtitle={summarizeFilters(preferenceFilters)}
        status={status}
        onToggle={onToggle}
        testId={jobFiltersTestIds.SAVED_SEARCHES_PREFERENCES_TOGGLE}
        trailing={<PreferencesEditLink />}
      />
      {showMismatchModal && (
        <PreferencesStateMismatchModal
          currentState={currentState}
          preferenceState={preferenceState}
          onClose={() => setShowMismatchModal(false)}
        />
      )}
    </>
  );
};

const PreferencesFilter = ({
  form,
  filterDefaults,
  activeSelection,
}: {
  form: UseFormReturn<FilterValues>;
  filterDefaults: FilterValues;
  activeSelection: ActiveSelection;
}) => {
  const { isLoaded, isAuthenticated, preferenceFilters, preferenceState } =
    usePreferenceFilters();

  if (!isLoaded) {
    return null;
  }

  if (!isAuthenticated) {
    return (
      <SavedFiltersRow
        icon={<User className="stroke-ink-subtle size-4" />}
        title="Your preferences"
        subtitle={"Create a profile to save preferences"}
        status={RowStatus.Disabled}
        onToggle={() => {}}
        testId={jobFiltersTestIds.SAVED_SEARCHES_PREFERENCES_TOGGLE}
        trailing={<PreferencesEditLink />}
      />
    );
  }

  if (preferenceFilters === null || preferenceState === null) {
    return null;
  }

  return (
    <ActivePreferencesRow
      form={form}
      filterDefaults={filterDefaults}
      preferenceFilters={preferenceFilters}
      preferenceState={preferenceState}
      activeSelection={activeSelection}
    />
  );
};

export default PreferencesFilter;
